import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProgressBar from '../components/ProgressBar';
import logoHeader from '../assets/logo_header.png';
import logosMobile from '../assets/logos_mobile.png';
import logosDesktop from '../assets/logos_desktop.png';
import iconYes from '../assets/icon_yes.png';
import bcaPayment from '../assets/bca-payment.png';
import mandiriPayment from '../assets/mandiri-payment.png';
import qrisPayment from '../assets/qris-payment.png';
import visaMcPayment from '../assets/visa-mc-payment.png';
import sslPayment from '../assets/ssl-payment.png';

interface FormData {
  fullName: string;
  email: string;
  whatsapp: string;
  selectedClass: string;
}

// Add batch configuration for countdown timer
const batch_config = {
  batch: 9,
  startDate: '2025-09-04T00:00:00',
  deadline: (() => {
    const now = new Date();
    // Get tomorrow's date in WIB
    const tomorrow = new Date(now.getTime() + (24 * 60 * 60 * 1000));
    const year = tomorrow.getFullYear();
    const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const day = String(tomorrow.getDate()).padStart(2, '0');
    // Return tomorrow at midnight in WIB format
    return `${year}-${month}-${day}T00:00:00`;
  })()
};

// Add CountdownTimer component
const CountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const calculateTimeLeft = () => {
      // Parse deadline as WIB time
      const deadlineDate = new Date(batch_config.deadline + '+07:00'); // Add WIB timezone
      const deadline = deadlineDate.getTime();
      const now = new Date().getTime();
      const distance = deadline - now;

      if (distance < 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      return {
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000)
      };
    };

    // Initial calculation
    setTimeLeft(calculateTimeLeft());

    // Update every second
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="flex justify-center gap-4 mt-4">
      <div className="text-center">
        <div className="text-2xl font-bold text-red-600">{String(timeLeft.days).padStart(2, '0')}</div>
        <div className="text-sm text-gray-600">Days</div>
      </div>
      <div className="text-center">
        <div className="text-2xl font-bold text-red-600">{String(timeLeft.hours).padStart(2, '0')}</div>
        <div className="text-sm text-gray-600">Hours</div>
      </div>
      <div className="text-center">
        <div className="text-2xl font-bold text-red-600">{String(timeLeft.minutes).padStart(2, '0')}</div>
        <div className="text-sm text-gray-600">Minutes</div>
      </div>
      <div className="text-center">
        <div className="text-2xl font-bold text-red-600">{String(timeLeft.seconds).padStart(2, '0')}</div>
        <div className="text-sm text-gray-600">Seconds</div>
      </div>
    </div>
  );
};

const Checkout = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>(() => {
    // Try to get saved form data from localStorage
    const savedData = localStorage.getItem('sjaFormData');
    return savedData ? JSON.parse(savedData) : {
      fullName: '',
      email: '',
      whatsapp: '',
      selectedClass: 'Penetrate Executive Status & Triple Digit Salary'
    };
  });
  
  const [formErrors, setFormErrors] = useState({
    fullName: '',
    email: '',
    whatsapp: '',
    selectedClass: ''
  });

  const classes = [
    { 
      title: '2x Salary in 3 Years PLUS 5x Salary in 10 years',
      audience: 'First Jobbers',
      price: 'Rp1.950.000',
      normal: 'Rp2.500.000',
      modules: '4 Modules',
      features: [
        'Access to Module 1, 2, 3, 4',
        'Lifetime access for Module Videos, previous batch Live Session Recordings',
        'Open Q&A anytime via private Discord Community',
        'Personalized Homework Review by Shaun Dju'
      ]
    },
    { 
      title: 'Penetrate Executive Status & Triple Digit Salary',
      audience: 'Managers',
      price: 'Rp2.950.000',
      normal: 'Rp3.500.000',
      modules: '5 Modules',
      features: [
        'Access to Module 1, 2, 3, 4, 5',
        'Lifetime access for Module Videos, previous batch Live Session Recordings',
        'Open Q&A anytime via private Discord Community',
        'Personalized Homework Review by Shaun Dju'
      ]
    },
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name as keyof typeof formErrors]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCourseClick = (courseTitle: string) => {
    setFormData(prev => ({
      ...prev,
      selectedClass: courseTitle
    }));
  };

  const validateForm = () => {
    const errors = {
      fullName: '',
      email: '',
      whatsapp: '',
      selectedClass: ''
    };
    let isValid = true;

    if (!formData.fullName.trim()) {
      errors.fullName = 'Full name is required';
      isValid = false;
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
      isValid = false;
    }

    if (!formData.whatsapp.trim()) {
      errors.whatsapp = 'WhatsApp number is required';
      isValid = false;
    }

    if (!formData.selectedClass) {
      errors.selectedClass = 'Please select a class';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Save form data to localStorage
    localStorage.setItem('sjaFormData', JSON.stringify(formData));
    
    // Create WhatsApp message
    const selectedCourse = classes.find(cls => cls.title === formData.selectedClass);
    const message = `Hi, I would like to enroll in the course:\n\n` +
      `Full Name: ${formData.fullName}\n` +
      `Email: ${formData.email}\n` +
      `WhatsApp: ${formData.whatsapp}\n` +
      `Selected Course: ${formData.selectedClass}\n` +
      `Price: ${selectedCourse?.price || 'N/A'}\n\n` +
      `Please help me with the enrollment process. Thank you!`;
    
    const whatsappUrl = `https://wa.me/6287841393601?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#FCFAF7]">
      {/* Progress Bar */}
      <ProgressBar currentStep={1} />
      
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <img 
            src={logoHeader} 
            alt="Shaun Dju Academy" 
            className="h-10 md:h-12"
          />
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Choose your Course Section */}
        <section className="mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-center text-[#203D32] mb-8">
            Choose Your Course
          </h1>
          
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {classes.map((item, idx) => (
              <div 
                key={idx} 
                className={`rounded-xl p-8 transition-all cursor-pointer border-2 ${
                  formData.selectedClass === item.title 
                    ? 'bg-[#203D32] text-white border-[#203D32]' 
                    : 'bg-white text-[#203D32] border-[#E5EFE7] hover:border-[#203D32]'
                }`}
                onClick={() => handleCourseClick(item.title)}
              >
                <h3 className="text-2xl font-bold mb-3">{item.title}</h3>
                <p className="text-lg mb-2">{item.audience}</p>
                
                {/* Price Object with crossed out normal price */}
                <div className="mb-6">
                  <p className={`line-through text-lg mb-1 ${
                    formData.selectedClass === item.title ? 'text-gray-300' : 'text-gray-400'
                  }`}>{item.normal}</p>
                  <p className="text-3xl font-semibold">{item.price}</p>
                </div>
                <p className={`text-lg mb-6 ${
                  formData.selectedClass === item.title ? 'text-gray-200' : 'text-gray-600'
                }`}>{item.modules}</p>
                
                <ul className="text-left space-y-4">
                  {item.features.map((feature, fidx) => (
                    <li key={fidx} className="flex items-center gap-3">
                      <img 
                        src={iconYes} 
                        alt="Included" 
                        className={`w-5 h-5 ${
                          formData.selectedClass === item.title ? 'brightness-200' : ''
                        }`}
                      />
                      <span className="text-lg">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Where Shaun Dju Academiers Work */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-[#203D32] mb-8">
            These are Where Shaun Dju Academiers Work
          </h2>
          <div className="text-center">
            <img 
              src={logosDesktop} 
              alt="Companies where our students work" 
              className="hidden md:block mx-auto max-w-full"
            />
            <img 
              src={logosMobile} 
              alt="Companies where our students work" 
              className="md:hidden mx-auto max-w-full"
            />
          </div>
        </section>

        {/* Order Form */}
        <section className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-[#203D32] mb-8">
              Order Form
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name *
                  </label>
                  <input 
                    type="text" 
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                    className={`border p-4 rounded-md w-full text-lg focus:ring-2 focus:ring-[#203D32] focus:border-transparent ${
                      formErrors.fullName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter your full name"
                  />
                  {formErrors.fullName && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.fullName}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input 
                    type="email" 
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className={`border p-4 rounded-md w-full text-lg focus:ring-2 focus:ring-[#203D32] focus:border-transparent ${
                      formErrors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter your email"
                  />
                  {formErrors.email && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.email}</p>
                  )}
                </div>
              </div>
              
              <div>
                <label htmlFor="whatsapp" className="block text-sm font-medium text-gray-700 mb-1">
                  WhatsApp Number *
                </label>
                <input 
                  type="tel" 
                  id="whatsapp"
                  name="whatsapp"
                  value={formData.whatsapp}
                  onChange={handleInputChange}
                  required
                  className={`border p-4 rounded-md w-full text-lg focus:ring-2 focus:ring-[#203D32] focus:border-transparent ${
                    formErrors.whatsapp ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your WhatsApp number"
                />
                {formErrors.whatsapp && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.whatsapp}</p>
                )}
              </div>

              <div>
                <label htmlFor="selectedClass" className="block text-sm font-medium text-gray-700 mb-1">
                  Selected Course *
                </label>
                <select 
                  id="selectedClass"
                  name="selectedClass"
                  value={formData.selectedClass}
                  onChange={handleInputChange}
                  required
                  className={`border p-4 rounded-md w-full text-lg focus:ring-2 focus:ring-[#203D32] focus:border-transparent ${
                    formErrors.selectedClass ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Choose Course</option>
                  {classes.map((cls, idx) => (
                    <option key={idx} value={cls.title}>{cls.title}</option>
                  ))}
                </select>
                {formErrors.selectedClass && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.selectedClass}</p>
                )}
              </div>

              {/* Price to be Paid */}
              {formData.selectedClass && (
                <div className="mt-6 p-6 bg-white border border-[#E5EFE7] rounded-lg">
                  <h3 className="text-xl font-semibold text-[#203D32] mb-4 text-center">Total Pembayaran</h3>
                  {(() => {
                    const selectedCourse = classes.find(cls => cls.title === formData.selectedClass);
                    if (!selectedCourse) return null;
                    
                    return (
                      <div className="text-center">
                        <p className="text-lg text-gray-500 line-through mb-2">{selectedCourse.normal}</p>
                        <p className="text-3xl font-bold text-[#203D32] mb-2">{selectedCourse.price}</p>
                        <p className="text-sm text-green-600 font-medium">
                          Hemat {(() => {
                            const normal = parseInt(selectedCourse.normal.replace(/[^\d]/g, ''));
                            const discounted = parseInt(selectedCourse.price.replace(/[^\d]/g, ''));
                            const savings = normal - discounted;
                            return `Rp${savings.toLocaleString('id-ID')}`;
                          })()}
                        </p>
                      </div>
                    );
                  })()}
                </div>
              )}

            {/* Countdown Timer */}
            <div className="mt-8 p-6 bg-gray-50 rounded-lg">
              <p className="text-center text-lg font-semibold text-[#203D32] mb-4">
                Harga Spesial berakhir dalam:
              </p>
              <CountdownTimer />
            </div>
              <p className="text-sm text-gray-500">
                Dengan membeli salah satu produk, anda dianggap menyetujui setiap ketentuan di website. 
                Semua pembayaran bersifat final dan tidak refundable. Akses untuk Discord & Jadwal Course 
                akan dikirim via WhatsApp resmi Shaun Dju Academy setelah pembayaran. Mohon dipastikan data yang akurat.
              </p>

              <div className="text-center">
                <button 
                  type="submit"
                  className="bg-[#25D366] text-white text-xl px-12 py-4 rounded-full font-semibold hover:opacity-90 transition flex items-center justify-center gap-2 mx-auto"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                  </svg>
                  Unlock my Academy Access
                </button>
                
                <div className="text-center mt-4 mb-6">
                  <p className="text-sm text-gray-500 mb-2">
                    100% secure - instant access after payment
                  </p>
                  <img src={sslPayment} alt="SSL Secured" className="h-12 mx-auto" />
                </div>
                
                {/* Payment Method Logos */}
                <div className="flex flex-wrap justify-center items-center gap-4">
                  <img src={bcaPayment} alt="BCA" className="h-8" />
                  <img src={mandiriPayment} alt="Mandiri" className="h-8" />
                  <img src={qrisPayment} alt="QRIS" className="h-8" />
                  <img src={visaMcPayment} alt="Visa & Mastercard" className="h-8" />

                </div>
              </div>
            </form>
            

          </div>
        </section>
      </div>
    </div>
  );
};

export default Checkout;
